import type { PublicUser } from '@nestjs-boilerplate/types';
import { redirect } from '@tanstack/react-router';

import { clearAuthState, getAuthState } from '@/stores/auth.store';

/**
 * Guards shared by every signed-in route group.
 *
 * `_authenticated` and the admin group both need these checks but render
 * different shells, so the checks live here rather than being inherited by
 * nesting one layout inside the other.
 */

/**
 * Redirects an unauthenticated or half-onboarded caller away from a signed-in
 * route.
 *
 * A null user means AuthProvider has not resolved the profile yet, not that the
 * caller is unauthorised — the onboarding checks are therefore skipped until it
 * loads. The token check still applies, because that is read synchronously from
 * storage.
 *
 * @param href - Current location, preserved as the post-login redirect target
 * @returns The signed-in user, or null while the profile is still loading, so
 *   a caller needing it for a further check does not have to read the store a
 *   second time
 * @throws A redirect when the caller may not proceed
 */
export function requireAuthenticated(href: string): PublicUser | null {
  const { accessToken, user } = getAuthState();

  if (!accessToken) {
    throw redirect({ to: '/login', search: { redirect: href } });
  }

  if (user && !user.emailVerified) {
    throw redirect({ to: '/verify-email' });
  }

  if (user && !user.accountSetupComplete) {
    throw redirect({ to: '/account-setup' });
  }

  // Fourth and last of the onboarding gates. Ordered after account setup
  // because a pending applicant is allowed — and required — to finish their
  // details first; the API enforces the same ordering in AccountStatusGuard.
  if (user && user.accountStatus === 'PENDING') {
    throw redirect({ to: '/awaiting-approval' });
  }

  // Rejected and disabled users have nothing to wait for. Login refuses them,
  // so this only fires for somebody disabled mid-session — clearing the stored
  // session is what stops them sitting on a rendered dashboard.
  if (user && (user.accountStatus === 'REJECTED' || user.accountStatus === 'DISABLED')) {
    clearAuthState();
    throw redirect({ to: '/login' });
  }

  return user;
}
